import { useEffect, useMemo, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { format } from "date-fns";
import { useDispatch } from "react-redux";
import { Text } from "react-native";

import { skipToken } from "@reduxjs/toolkit/query";

import {
  useAddUrgentConcernCommentMutation,
  useGetUrgentConcernQuery,
} from "app/services/api/shiftHandoverApi";
import { urgentConcernsApi } from "app/services/api/urgentConcernsApi";
import { UrgentConcernModel } from "app/services/api/types/shiftHandover/models/UrgentConcernModel";
import { UrgentConcernCommentModel } from "app/services/api/types/shiftHandover/models/UrgentConcernCommentModel";
import { UrgentConcernStatusEnum } from "app/services/api/types/shiftHandover/enums/UrgentConcernStatusEnum";
import { getUrgentConcernReporterName } from "app/features/urgentConcerns/utils/getUrgentConcernReporterName";
import { UrgentConcernTypeBadge } from "app/features/shiftHandover/atoms/UrgentConcernTypeBadge";
import { useDisplayToast } from "ui/hooks/useDisplayToast";
import { ReusableModal } from "ui/molecules/modal/ReusableModal";
import { FormControlledInputTextArea } from "ui/molecules/form/FormControlledInputTextArea";
import { Button, ButtonText } from "ui/gluestack/button";
import { View } from "ui/atoms/view";
import Pagination from "ui/molecules/pagination/Pagination";

const COMMENTS_PER_PAGE = 5;

interface Props {
  concern: UrgentConcernModel | null;
  onClose: () => void;
}

interface FormValues {
  comment: string;
}

const sortByCreatedAtAsc = (
  a: UrgentConcernCommentModel,
  b: UrgentConcernCommentModel,
) => {
  const aTime = a.created_at ? new Date(a.created_at).getTime() : 0;
  const bTime = b.created_at ? new Date(b.created_at).getTime() : 0;
  return aTime - bTime;
};

const formatTimestamp = (value?: string | null) => {
  if (!value) return "";
  try {
    return format(new Date(value), "dd MMM yyyy HH:mm");
  } catch {
    return "";
  }
};

const getInitials = (name?: string | null) => {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

export const CommentUrgentConcernModal = ({ concern, onClose }: Props) => {
  const [page, setPage] = useState(1);

  const [addUrgentConcernComment, { isLoading }] =
    useAddUrgentConcernCommentMutation();
  const { showNewToast, showErrorToast } = useDisplayToast();
  const dispatch = useDispatch();

  // The id used both for fetching and posting must match — use the polymorphic
  // reportable_id when present, falling back to the concern row id.
  const effectiveConcernId = concern
    ? (concern.reportable_id ?? concern.id)
    : null;

  const { data: fetchedConcern } = useGetUrgentConcernQuery(
    effectiveConcernId ?? skipToken,
    { refetchOnMountOrArgChange: true },
  );

  // Prefer the freshly fetched concern (with full comments thread + latest
  // status) once available; fall back to the prop while loading so the modal
  // header renders immediately without flicker.
  const activeConcern = fetchedConcern ?? concern;

  const { control, handleSubmit, reset } = useForm<FormValues>({
    defaultValues: { comment: "" },
  });
  const commentValue = useWatch({ control, name: "comment" });
  const isCommentEmpty = !commentValue || !commentValue.trim();

  const sortedComments = useMemo(() => {
    if (!activeConcern?.comments?.length) return [];
    return [...activeConcern.comments].sort(sortByCreatedAtAsc);
  }, [activeConcern]);

  const totalPages = Math.max(
    1,
    Math.ceil(sortedComments.length / COMMENTS_PER_PAGE),
  );

  // Reset the form and snap to the last page whenever a new concern is opened
  // or the comments list grows (e.g. after a successful POST + refetch).
  const concernKey = effectiveConcernId;
  const commentsCount = sortedComments.length;
  useEffect(() => {
    if (concernKey === null) {
      reset({ comment: "" });
      setPage(1);
      return;
    }
    setPage(Math.max(1, Math.ceil(commentsCount / COMMENTS_PER_PAGE)));
  }, [concernKey, commentsCount, reset]);

  const handleClose = () => {
    reset({ comment: "" });
    setPage(1);
    onClose();
  };

  const isResolved = activeConcern?.status === UrgentConcernStatusEnum.RESOLVED;

  const visibleComments = useMemo(() => {
    const start = (page - 1) * COMMENTS_PER_PAGE;
    return sortedComments.slice(start, start + COMMENTS_PER_PAGE);
  }, [sortedComments, page]);

  const paginationMeta = useMemo(
    () => ({
      current_page: page,
      first_page_url: "",
      from:
        sortedComments.length === 0 ? 0 : (page - 1) * COMMENTS_PER_PAGE + 1,
      last_page: totalPages,
      last_page_url: "",
      next_page_url: page < totalPages ? "" : null,
      prev_page_url: page > 1 ? "" : null,
      path: "",
      per_page: COMMENTS_PER_PAGE,
      to: Math.min(page * COMMENTS_PER_PAGE, sortedComments.length),
      total: sortedComments.length,
    }),
    [page, totalPages, sortedComments.length],
  );

  const submit = handleSubmit(async ({ comment }) => {
    if (!effectiveConcernId) return;
    const trimmed = comment.trim();
    if (!trimmed) return;
    try {
      await addUrgentConcernComment({
        params: { concernId: effectiveConcernId },
        body: { body: trimmed },
      }).unwrap();
      // `shiftHandoverApi` invalidates its own tags automatically; nudge the
      // resident-scoped query (separate API instance) so the urgent-concerns
      // table also refetches and the modal re-renders with the fresh thread.
      dispatch(urgentConcernsApi.util.invalidateTags(["UrgentConcerns"]));
      showNewToast({
        title: "Comment added",
        message: "The comment has been added to this concern.",
        type: "success",
      });
      reset({ comment: "" });
    } catch (e) {
      showErrorToast(e);
    }
  });

  const composer = !isResolved ? (
    <View className="border-t border-border-200 bg-white px-10 py-4 gap-2">
      <FormControlledInputTextArea
        id="urgent-concern-comment"
        name="comment"
        control={control}
        label="Write a comment"
        placeholder="Share an update on this concern…"
        isRequired
      />
      <View className="flex-row gap-2 justify-end">
        <Button variant="outline" onPress={handleClose} disabled={isLoading}>
          <ButtonText>Cancel</ButtonText>
        </Button>
        <Button onPress={submit} disabled={isLoading || isCommentEmpty}>
          <ButtonText>{isLoading ? "Posting…" : "Add comment"}</ButtonText>
        </Button>
      </View>
    </View>
  ) : (
    <View className="border-t border-border-200 bg-background-50 px-10 py-4">
      <Text className="text-sm font-semibold text-typography-700">
        This concern has been resolved
      </Text>
      <Text className="text-xs text-typography-500 mt-1">
        {activeConcern?.resolved_by?.name
          ? `Resolved by ${activeConcern.resolved_by.name}${activeConcern.resolved_at ? ` on ${formatTimestamp(activeConcern.resolved_at)}` : ""}.`
          : "New comments can no longer be added."}
      </Text>
    </View>
  );

  const reporterName = activeConcern
    ? getUrgentConcernReporterName(activeConcern)
    : "";

  return (
    <ReusableModal
      isVisible={concern !== null}
      onClose={handleClose}
      title="Concern Comments"
      hasCancelButton
      className="w-[640px] max-w-[90vw]"
      footer={composer}
    >
      <View className="gap-4">
        {activeConcern ? (
          <View className="rounded-lg border border-border-200 bg-background-50 p-3 gap-2">
            <View className="flex-row items-center gap-2 flex-wrap">
              <UrgentConcernTypeBadge type={activeConcern.type} />
              <Text className="text-xs text-typography-500">
                Reported by{" "}
                <Text className="font-semibold text-typography-700">
                  {reporterName}
                </Text>
                {activeConcern.created_at
                  ? ` · ${formatTimestamp(activeConcern.created_at)}`
                  : ""}
              </Text>
            </View>
            <Text className="text-sm text-typography-800 first-letter:uppercase">
              {activeConcern.description}
            </Text>
          </View>
        ) : null}

        <View className="gap-3">
          <View className="flex-row items-center gap-2">
            <Text className="text-base font-semibold">Comments</Text>
            {sortedComments.length > 0 ? (
              <View className="px-2 py-0.5 rounded-full bg-background-100">
                <Text className="text-xs font-semibold text-typography-700">
                  {sortedComments.length}
                </Text>
              </View>
            ) : null}
          </View>

          {sortedComments.length === 0 ? (
            <View className="items-center py-8 gap-1">
              <Text className="text-sm font-semibold text-typography-600">
                No comments yet
              </Text>
              <Text className="text-xs text-typography-500">
                {isResolved
                  ? "This concern was resolved without any comments."
                  : "Be the first to add an update on this concern."}
              </Text>
            </View>
          ) : (
            visibleComments.map((c) => (
              <View
                key={c.id}
                className="flex-row gap-3 rounded-md border border-border-200 bg-background-50 p-3"
              >
                <View className="h-8 w-8 rounded-full bg-primary-100 items-center justify-center">
                  <Text className="text-xs font-semibold text-primary-700">
                    {getInitials(c.author?.name)}
                  </Text>
                </View>
                <View className="flex-1 gap-1">
                  <View className="flex-row justify-between items-center">
                    <Text className="text-sm font-semibold text-typography-800">
                      {c.author?.name ?? "Unknown"}
                    </Text>
                    <Text className="text-xs text-typography-500">
                      {formatTimestamp(c.created_at)}
                    </Text>
                  </View>
                  <Text className="text-sm text-typography-700">{c.body}</Text>
                </View>
              </View>
            ))
          )}

          {sortedComments.length > COMMENTS_PER_PAGE ? (
            <Pagination meta={paginationMeta} onPageChange={setPage} />
          ) : null}
        </View>
      </View>
    </ReusableModal>
  );
};

export default CommentUrgentConcernModal;
